import React, { useCallback, useMemo } from "react";
import { Box, Typography } from "@mui/material";

// Local Fleet Performance Components
import FleetBreakdownChart from "../Components/FleetBreakdownChart";
import { useFleetChartDrillDown } from "../Components/FleetChartDrillDown/useFleetChartDrillDown";
import { formatDateRangeWithSmartYear } from "../../../Helper/DatePicker/DateFormatters";
import { getFleetPerformanceAnalyticsList } from "../../../Api/FleetPerformanceApi";
import { FLEET_CHART_CONFIG, ENERGY_CONSUMPTION_BUCKETS } from "./FleetChartConfig";
import NumberFormatter from "../../../Helper/Formatters/NumberFormatter";
import {
    FLEET_PERFORMANCE_CHART_KEYS,
    resolveFleetGroupByField,
    useFleetBreakdownDimensions,
} from "../fleetBreakdownDimensions";

// --- Main Component: EnergyConsumptionBreakdownChart ---

const EnergyConsumptionBreakdownChart = ({ startDate, endDate, globalFilters }) => {

    const groupByOptions = useFleetBreakdownDimensions(
        FLEET_PERFORMANCE_CHART_KEYS.ENERGY_CONSUMPTION_BREAKDOWN
    );
    const defaultGroupBy = groupByOptions[0]?.value;

    const handleFetchSidebarData = useCallback(async (groupBy) => {
        try {
            // The dimension's own value is the backend field; see resolveFleetGroupByField
            const backendGroupBy = resolveFleetGroupByField(groupBy);

            const apiParams = {
                groupBy: [backendGroupBy],
                select: ['DistinctVehicleCount'],
                startDate,
                endDate,
                sortBy: JSON.stringify([{ column: 'DistinctVehicleCount', direction: 'DESC' }]),
                _ts: Date.now() // Trace request
            };

            const response = await getFleetPerformanceAnalyticsList(globalFilters, apiParams);

            if (response && response.responseStatus && response.responseStatus.code === 200) {
                const data = response.response?.data || [];

                const items = data.map(item => {
                    // Sum all values in metricsArray to get total count
                    const totalCountForCity = (item.metricsArray || []).reduce((sum, m) => sum + (m.value || 0), 0);

                    return {
                        id: item.xLabel,
                        label: item.xLabel,
                        count: totalCountForCity
                    };
                });

                // Calculate total count for 'All'
                const totalCount = items.reduce((sum, item) => sum + (item.count || 0), 0);
                const allItem = { id: 'all', label: 'All', count: totalCount };

                return { items: [allItem, ...items] };
            }
            return { items: [] };

        } catch (error) {
            console.error("Error fetching sidebar data", error);
            return { items: [] };
        }
    }, [startDate, endDate, globalFilters]);

    const handleFetchChartData = useCallback(async (groupBy, selectedItems) => {

        try {
            // The dimension's own value is the backend field; see resolveFleetGroupByField
            const backendGroupBy = resolveFleetGroupByField(groupBy);

            const isAllSelected = selectedItems.some(i => i.id === 'all');
            let filters = [];

            if (!isAllSelected && selectedItems.length > 0) {
                filters.push({
                    name: backendGroupBy,
                    values: selectedItems.map(i => i.id)
                });
            }

            const apiParams = {
                // Always group by ProjectName to show battery configs on categories
                groupBy: ['ProjectName', 'AverageEnergyConsumedBucket'],
                select: ['DistinctVehicleCount'],
                startDate,
                endDate,
                filters: filters.length > 0 ? filters : undefined,
                sortBy: JSON.stringify([{ column: 'DistinctVehicleCount', direction: 'DESC' }]),
            };

            const response = await getFleetPerformanceAnalyticsList(globalFilters, apiParams);

            if (response && response.responseStatus && response.responseStatus.code === 200) {
                const data = response.response?.data || [];

                // Transform data for chart
                const transformed = data.map(item => {
                    const metrics = item.metricsArray || [];

                    // Helper to safely get value
                    const getValue = (bucket) => {
                        const m = metrics.find(metric => metric.type === bucket);
                        return m ? m.value : 0;
                    };

                    return {
                        label: item.xLabel,
                        ...Object.fromEntries(
                            ENERGY_CONSUMPTION_BUCKETS.map((b) => [b.backendValue, getValue(b.backendValue)])
                        ),
                    };
                });
                return transformed;
            }
            return [];
        } catch (error) {
            console.error("Error fetching chart data", error);
            return [];
        }
    }, [startDate, endDate, globalFilters]);

    // --- Chart Config ---

    const getChartConfig = useCallback((chartData) => {
        if (!chartData || chartData.length === 0) return { series: [], options: {} };

        const categories = chartData.map(d => d.label);

        return {
            series: ENERGY_CONSUMPTION_BUCKETS.map((b) => ({
                name: b.label,
                data: chartData.map(d => d[b.backendValue]),
            })),
            options: {
                chart: { id: "energy-consumption-breakdown-chart", stacked: true },
                colors: ENERGY_CONSUMPTION_BUCKETS.map((b) => b.color),
                xaxis: {
                    categories: categories,
                    labels: {
                        show: true,
                        formatter: (val) => NumberFormatter(val, 1)
                    }
                },
                yaxis: {
                    labels: { style: { fontSize: '12px', fontWeight: 400, colors: '#686868' } }
                },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: "40%",
                        borderRadius: 2,
                        dataLabels: {
                            total: {
                                enabled: true,
                                formatter: (val) => NumberFormatter(val, 1)
                            }
                        }
                    }
                },
                dataLabels: {
                    enabled: false
                },
                tooltip: {
                    y: {
                        formatter: (val) => NumberFormatter(val, 1) + (val === 1 ? " vehicle" : " vehicles"),
                        title: { formatter: (seriesName) => seriesName + ":" }
                    }
                }
            }
        };
    }, []);

    const drillDown = useFleetChartDrillDown({
        startDate,
        endDate,
        globalFilters,
        config: FLEET_CHART_CONFIG.ENERGY_CONSUMPTION_BREAKDOWN,
    });

    const drillDownConfig = useMemo(() => ({
        ...FLEET_CHART_CONFIG.ENERGY_CONSUMPTION_BREAKDOWN,
        handleDataPointClick: drillDown.handleDataPointClick,
    }), [drillDown.handleDataPointClick]);

    const fleetBreakdownConfig = useMemo(() => ({
        onFetchSidebarData: handleFetchSidebarData,
        onFetchChartData: handleFetchChartData,
        getChartConfig,
        customLegend: {
            chartId: "energy-consumption-breakdown-chart",
            label: "Project",
            series: ENERGY_CONSUMPTION_BUCKETS.map((b) => ({ name: b.label, color: b.color })),
            centered: true
        }
    }), [handleFetchSidebarData, handleFetchChartData, getChartConfig]);

    return (
        <FleetBreakdownChart
            minVisibilityHeight={400}
            title="Fleet Energy Consumption per km Breakdown"
            // FleetBreakdownChart seeds its groupBy state from these props once, on
            // mount, and the page's meta lands after that. Keying on the default makes
            // it re-seed for an org whose config drops the dimension it mounted with;
            // for every other org the key is the same string before and after, so
            // nothing remounts and nothing refetches.
            key={defaultGroupBy}
            groupByOptions={groupByOptions}
            defaultGroupBy={defaultGroupBy}
            xAxisTitle="Number of Vehicles"
            listHeader="NO. OF VEHICLES"
            config={fleetBreakdownConfig}
            dependencies={[startDate, endDate, globalFilters]}
            drillDownConfig={drillDownConfig}
        />
    );
};

export default EnergyConsumptionBreakdownChart;